import {Component, computed, effect} from '@angular/core';
import {
  ExplorationService,
  QuestsCrudService
} from 'quest-atlas-angular-components';
import { rxResource } from '@angular/core/rxjs-interop';
import Chart from 'chart.js/auto';
import { map } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';


@Component({
  selector: 'app-statistics',
  templateUrl: './statistics.component.html',
  styleUrl: './statistics.component.scss',
  standalone: false
})
export class StatisticsComponent {
  myQuestsCount = rxResource({
    stream: () => this.questsService.countMyQuests$()
  });
  myPublicQuestsCount = rxResource({
    stream: () => this.questsService.countMyQuests$(true)
  });

  countMyQuestsStartedCompleted = rxResource({
    stream: () => this.questsService.countMyQuestsStartedCompleted$()
  });

  completionPercentage = computed(() => {
    const stats = this.countMyQuestsStartedCompleted.value();

    if (!stats) {
      return 0;
    }

    const { started, completed } = stats;

    if (started === 0) {
      return 0;
    }

    return Math.round((completed / started) * 100);
  });

  completionPercentageColor = computed(() => {
    const percentage = this.completionPercentage();

    if (percentage >= 75) {
      return '--atlas-color-green';
    } else if (percentage >= 50) {
      return '--atlas-color-warning';
    } else {
      return '--atlas-color-red';
    }
  });

  countMyQuestsStatsByDate = rxResource({
    stream: () => this.questsService.countMyQuestsStatsByDate$()
  });

  countMyObelisks = rxResource({
    stream: () => this.explorationService.countMyObelisks$().pipe(map(value => value?.count || 0))
  });

  countMyApprovedObelisks = rxResource({
    stream: () => this.explorationService.countMyObelisks$(true).pipe(map(value => value?.count || 0))
  });

  statsMyObelisks = rxResource({
    stream: () => this.explorationService.statsMyObelisksByDate$()
  });

  constructor(private questsService: QuestsCrudService, private explorationService: ExplorationService, private transloco: TranslocoService) {
    effect(() => {
      if (!this.countMyQuestsStatsByDate.value() || this.countMyQuestsStatsByDate.value().length === 0) {
        return;
      }

      const ctx = document.getElementById('quests-progress-chart') as HTMLCanvasElement;

      let labels = [];
      let completedData = [];
      let startedData = [];

      this.countMyQuestsStatsByDate.value().forEach(val => {
        labels.push(val.date.toLocaleDateString());
        completedData.push(val.completed);
        startedData.push(val.started);
      })

      const chart = new Chart(ctx, {
        data: {
          datasets: [{
            type: 'bar',
            label: this.transloco.translate('completedData'),
            data: completedData,
            barPercentage: 0.5, // Make bars thinner
            categoryPercentage: 0.5 // Make bars thinner
          }, {
            type: 'line',
            label: this.transloco.translate('started'),
            data: startedData,
          }],
          labels
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              ticks: {
                stepSize: 1,
                callback: function(value) {
                  if (Number.isInteger(value)) {
                    return value;
                  }
                  return null;
                }
              },
              beginAtZero: true
            }
          }
        }
      });
    });

    effect(() => {
      if (this.countMyObelisks.isLoading() || this.countMyApprovedObelisks.isLoading() || this.statsMyObelisks.isLoading()) {
        return;
      }

      const ctx = document.getElementById('obelisks-progress-chart') as HTMLCanvasElement;

      let labels = [];
      let visitedData = [];

      Object.entries(this.statsMyObelisks.value().stats).forEach(val => {
        labels.push(new Date(val[0]).toLocaleDateString());
        visitedData.push(val[1]);
      })

      const chart = new Chart(ctx, {
        data: {
          datasets: [{
            type: 'line',
            label: this.transloco.translate('visitedData'),
            data: visitedData,
          }],
          labels
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              ticks: {
                stepSize: 1,
                callback: function(value) {
                  if (Number.isInteger(value)) {
                    return value;
                  }
                  return null;
                }
              },
              beginAtZero: true
            }
          }
        }
      });
    });
  }
}
